import { FC, useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TableDataQa } from 'commons/types/enums'
import { Request } from 'commons/utils/request'
import { Accordion, ButtonGroup, Layer, Link, Table, Toast, Tooltip } from 'ui/components'
import { TextButton } from 'ui/components/Buttons'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'
import { ResourcePath } from 'ui/temp/ResourcePath'

import { handlerServerError } from '@orbit/errors'

import { HRR_DETAILS_KEY } from '../../../../query'
import { SalesSourceItem } from '../../../../types'
import { columns } from './columns'
import { SendTaskDialog } from './components'

export const License: FC<{ salesSourceItem: SalesSourceItem; highRiskReviewId: number }> = (props) => {
  const { salesSourceItem, highRiskReviewId } = props
  const { licenseId, licenseNumber, sources, enableOverride, showOverrideButtonTooltip, enableSendTask } =
    salesSourceItem
  const [showSendTaskDialog, setShowSendTaskDialog] = useState(false)

  const queryClient = useQueryClient()
  const confirmation = useConfirmation()

  const { mutate: cancelTask } = useMutation<void, Error, { customAnalyticsId: number; licenseId: number }>({
    mutationFn: (opts) => Request.post(Request.urls.bank.salesDataClientSourceTaskCancellation, opts),
    onSuccess: () => {
      Toast.successSmall('Notes have been saved.')
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  const { mutate: deleteTask } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.delete(Request.urls.bank.salesDataBankSource, opts),
    onSuccess: () => {
      Toast.successSmall('Notes have been saved.')
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  const handleTaskCancel = async () => {
    await confirmation.showDeleteConfirmation({
      message: `Are you sure you want to Cancel Task?`,
      submitButtonText: 'Cancel Task',
      confirmCallback: async () => cancelTask({ licenseId, customAnalyticsId: highRiskReviewId })
    })
  }

  const handleDelete = async (id: number) => {
    await confirmation.showDeleteConfirmation({
      message: `Are you sure you want to proceed? Report will be recalculated.`,
      submitButtonText: 'Confirm',
      confirmCallback: async () => deleteTask({ id })
    })
  }

  return (
    <Accordion
      expanded
      bottomPadding="small"
      title={
        <>
          License&nbsp;
          <ResourcePath finishedPath={`/licenses/${licenseId}`} name={licenseNumber} id={licenseId} />
        </>
      }
      actions={
        <ButtonGroup margin="small">
          {!enableOverride && showOverrideButtonTooltip?.length > 0 && (
            <Tooltip id={`source-edit-${licenseNumber}`} type="dark" content={showOverrideButtonTooltip}>
              <TextButton disabled>Add Manual Override</TextButton>
            </Tooltip>
          )}
          {enableOverride && (
            <Link
              to="/salesdata/bank-source-override/$mrId/$licenseId"
              params={{ mrId: highRiskReviewId.toString(), licenseId: licenseId.toString() }}
              search={{ type: 'custom' }}
            >
              <TextButton>Add Manual Override</TextButton>
            </Link>
          )}
          {enableSendTask && (
            <TextButton
              onClick={(e) => {
                e.stopPropagation()
                setShowSendTaskDialog(true)
              }}
            >
              Send Task
            </TextButton>
          )}
        </ButtonGroup>
      }
    >
      {showSendTaskDialog && (
        <SendTaskDialog
          licenseId={licenseId}
          customAnalyticsId={highRiskReviewId}
          onClose={() => setShowSendTaskDialog(false)}
        />
      )}
      <Layer shadowed rounded stretch>
        <Table
          dataQa={TableDataQa.BP_HRR_BANK_ACCOUNT_SALES_SOURCE}
          columns={columns(handleTaskCancel, handleDelete, highRiskReviewId, licenseId)}
          dataSource={sources?.map((source) => ({ item: source }))}
        />
      </Layer>
    </Accordion>
  )
}
